import { useState, useRef, useEffect } from 'react';
import axios from 'axios'; // ✅ Fix 1: added missing import
import { useActiveStore } from '../../context/ActiveStoreContext';
import { useNavigate } from 'react-router-dom';
import { Store, ChevronDown, Check, Loader2, List, AlertCircle } from 'lucide-react';
import Loader from '../common/Loader';

interface StoreSwitcherProps {
  className?: string;
  compact?: boolean;
}

// ✅ Fix 2: added `message` field to the union type
type ConfirmAction =
  | { type: 'delete'; data?: any }
  | { type: string; message?: string; data?: any };

export default function StoreSwitcher({ className = '', compact = false }: StoreSwitcherProps) {
  const navigate = useNavigate();
  const { activeStore, userStores, switchToStore, isLoading } = useActiveStore();
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [isResending, setIsResending] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const storesForDisplay = userStores || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!storesForDisplay || storesForDisplay.length === 0) {
    return null;
  }

  const handleStoreSelect = async (storeId: number) => {
    if (storeId === activeStore?.id || isLoading) return;

    const store = storesForDisplay.find(s => s.id === storeId);

    if (store?._isPending && store._invitationToken) {
      try {
        const response = await axios.post('/api/invitations/accept', {
          token: store._invitationToken,
        });
        if (response.data.success) {
          window.location.reload();
        } else {
          console.error('Failed to accept invitation:', response.data.message);
        }
      } catch (error) {
        console.error('Error accepting invitation:', error);
      }
      return;
    }

    try {
      await switchToStore(storeId);
      setIsOpen(false);
      localStorage.removeItem('cachedPermissions');
      window.dispatchEvent(new CustomEvent('storeChanged', {
        detail: { storeId, timestamp: Date.now() },
      }));
      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      console.error('Failed to switch store:', error);
    }
  };

  const handleViewAll = () => {
    navigate('/stores');
    setIsOpen(false);
  };

  const getRoleBadgeColor = (role?: string, isPending?: boolean) => {
    if (isPending) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    switch (role) {
      case 'owner':
      case 'admin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'manager':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getRoleLabel = (role?: string, isPending?: boolean) => {
    if (isPending) return 'Pending';
    switch (role) {
      case 'owner': return 'Owner';
      case 'admin': return 'Admin';
      case 'manager': return 'Manager';
      default: return 'Member';
    }
  };

  const displayName =
    activeStore?.name ||
    activeStore?.shop?.replace('.myshopify.com', '') ||
    'Select Store';

  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };
  // ✅ Fix 3: ConfirmDialog is now rendered inside the same return, wrapped in a fragment
  if (compact) {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-2 lg:px-3 py-2 text-sm font-medium text-gray-700 dark:text-[#E4E7EC] hover:bg-gray-100 dark:hover:bg-[#080f1c] rounded-lg transition-colors"
          disabled={isLoading}
        >
          {/* Always show store icon */}
          <Store className="w-4 h-4" />

          {/* ✅ Mobile → initials */}
          <span className="lg:hidden text-sm font-medium">
            {getInitials(displayName)}
          </span>

          {/* ✅ Desktop → full name */}
          <span className="hidden lg:block truncate max-w-32">
            {displayName}
          </span>
          <ChevronDown
            className={`w-4 h-4 transition-transform ml-1 ${isOpen ? 'rotate-180' : ''
              }`}
          />

          {isLoading && <Loader className="w-20 h-20" />}
        </button>

        {isOpen && (
          <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-80 bg-white dark:bg-[#080f1c] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
            <div className="p-1">
              <div className="px-1 py-1 text-sm font-semibold text-gray-500 dark:text-[#E4E7EC] uppercase tracking-smaller">
                Switch Store
              </div>

              <div className="mt-1 space-y-1">
                {storesForDisplay.map((store) => (
                  <button
                    key={store.id}
                    onClick={() => handleStoreSelect(store.id)}
                    className={`w-full flex items-center justify-between p-1 rounded-lg text-left transition-colors ${store.id === activeStore?.id
                        ? 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800'
                        : 'hover:bg-gray-200 dark:hover:bg-[#111f33]'
                      }`}
                    disabled={isLoading}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-gray-100 dark:bg-[#111f33] rounded-md flex items-center justify-center">
                        <Store className="w-3 h-3 text-gray-600 dark:text-[#E4E7EC]" />
                      </div>

                      <div className="font-medium text-gray-900 dark:text-[#E4E7EC]">
                        {store.name || store.shop.replace('.myshopify.com', '')}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {store.shop === activeStore?.shop && (
                        <Check className="w-4 h-4 text-teal-600" />
                      )}
                    </div>
                  </button>
                ))}

                <hr className="border-t border-gray-200 dark:border-gray-700" />

                <button
                  onClick={handleViewAll}
                  className="w-full flex items-center justify-between p-1 rounded-lg text-left transition-colors hover:bg-gray-200 dark:hover:bg-[#111f33]"
                  disabled={isLoading}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-teal-100 dark:bg-teal-900/30 rounded-md flex items-center justify-center">
                      <List className="w-3 h-3 text-teal-600 dark:text-teal-400" />
                    </div>

                    <div className="font-medium text-gray-900 dark:text-[#E4E7EC]">
                      View All Stores
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
    // return (
    //   <>
    //     <div className={`relative ${className}`} ref={dropdownRef}>
    //       <button
    //         onClick={() => setIsOpen(!isOpen)}
    //         className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-[#E4E7EC] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
    //         disabled={isLoading}
    //       >
    //         <Store className="w-4 h-4" />
    //         <span className="truncate max-w-32">
    //           {activeStore?.name || activeStore?.shop?.replace('.myshopify.com', '') || 'Select Store'}
    //         </span>
    //         <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    //         {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
    //       </button>

    //       {isOpen && (
    //         <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-80 bg-white dark:bg-[#080f1c] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">              <div className="p-1">
    //           <div className="px-1 py-1 text-sm font-semibold text-gray-500 dark:text-[#E4E7EC] uppercase tracking-smaller">
    //             Switch Store
    //           </div>
    //           <div className="mt-1 space-y-1">
    //             {storesForDisplay.map((store) => (
    //               <button
    //                 key={store.id}
    //                 onClick={() => handleStoreSelect(store.id)}
    //                 className={`w-full flex items-center justify-between p-1 rounded-lg text-left transition-colors ${store.id === activeStore?.id
    //                     ? 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800'
    //                     : 'hover:bg-gray-200 dark:hover:bg-[#111f33]'
    //                   }`}
    //                 disabled={isLoading}
    //               >
    //                 <div className="flex items-center space-x-3">
    //                   <div className="w-6 h-6 bg-gray-100 dark:bg-[#111f33] rounded-md flex items-center justify-center">
    //                     <Store className="w-3 h-3 text-gray-600 dark:text-[#E4E7EC]" />
    //                   </div>
    //                   <div className="font-medium text-gray-900 dark:text-[#E4E7EC]">
    //                     {store.name || store.shop.replace('.myshopify.com', '')}
    //                   </div>
    //                 </div>
    //                 <div className="flex items-center space-x-2">
    //                   {/* <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(store.user_role, store._isPending)}`}>
    //                       {getRoleLabel(store.user_role, store._isPending)}
    //                     </span> */}
    //                   {store.shop === activeStore?.shop && (
    //                     <Check className="w-4 h-4 text-teal-600" />
    //                   )}
    //                 </div>
    //               </button>
    //             ))}
    //             <hr className="border-t border-gray-200 dark:border-gray-700" />
    //             <button
    //               onClick={handleViewAll}
    //               className="w-full flex items-center justify-between p-1 rounded-lg text-left transition-colors hover:bg-gray-200 dark:hover:bg-[#111f33]"
    //               disabled={isLoading}
    //             >
    //               <div className="flex items-center space-x-3">
    //                 <div className="w-6 h-6 bg-teal-100 dark:bg-teal-900/30 rounded-md flex items-center justify-center">
    //                   <List className="w-3 h-3 text-teal-600 dark:text-teal-400" />
    //                 </div>
    //                 <div className="font-medium text-gray-900 dark:text-[#E4E7EC]">
    //                   View All Stores
    //                 </div>
    //               </div>
    //             </button>
    //           </div>
    //         </div>
    //         </div>
    //       )}
    //     </div>

    //     {/* ✅ Confirm dialog is now a sibling, not orphaned outside the return */}
    //     {showConfirmDialog && (
    //       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    //         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
    //           <div className="flex items-center mb-4">
    //             <AlertCircle className="w-6 h-6 text-amber-500 mr-3" />
    //             <div>
    //               <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
    //                 {confirmAction?.type === 'delete' ? 'Confirm Delete' : 'Confirm Action'}
    //               </h3>
    //               <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
    //                 {confirmAction?.type === 'delete'
    //                   ? 'Are you sure you want to delete this item? This action cannot be undone.'
    //                   : (confirmAction as any)?.message || 'Are you sure you want to proceed?'}
    //               </p>
    //             </div>
    //           </div>
    //           <div className="flex justify-end space-x-3">
    //             <button
    //               onClick={() => { setShowConfirmDialog(false); setConfirmAction(null); }}
    //               className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
    //             >
    //               Cancel
    //             </button>
    //             <button
    //               onClick={() => {
    //                 if (confirmAction?.type === 'delete') {
    //                   console.log('Deleting item:', (confirmAction as any).data);
    //                 }
    //                 setShowConfirmDialog(false);
    //                 setConfirmAction(null);
    //               }}
    //               className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
    //             >
    //               {confirmAction?.type === 'delete' ? 'Delete' : 'Confirm'}
    //             </button>
    //           </div>
    //         </div>
    //       </div>
    //     )}
    //   </>
    // );
  }

  // ── Full (non-compact) variant ──────────────────────────────────
  return (
    <>
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-3 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow"
          disabled={isLoading}
        >
          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
            <Store className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </div>
          <div className="flex-1 text-left">
            <div className="font-medium text-gray-900 dark:text-white">
              {activeStore?.name || activeStore?.shop?.replace('.myshopify.com', '') || 'No Store Selected'}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {activeStore?.shop || 'Select a store to continue'}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {activeStore && (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(activeStore.user_role)}`}>
                {getRoleLabel(activeStore.user_role)}
              </span>
            )}
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            {isLoading && <Loader className="w-20 h-20" />}
          </div>
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 w-80 bg-white dark:bg-[#0d1828] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Your Stores ({storesForDisplay.length})
              </div>
              <div className="mt-1 space-y-1 max-h-64 overflow-y-auto">
                <button
                  onClick={handleViewAll}
                  className="w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                  disabled={isLoading}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                      <List className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div className="font-medium text-gray-900 dark:text-white">View All Stores</div>
                  </div>
                </button>

                {storesForDisplay.map((store) => (
                  <button
                    key={store.id}
                    onClick={() => handleStoreSelect(store.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${store.id === activeStore?.id
                      ? 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    disabled={isLoading}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                        <Store className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {store.name || store.shop.replace('.myshopify.com', '')}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{store.shop}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(store.user_role, store._isPending)}`}>
                        {getRoleLabel(store.user_role, store._isPending)}
                      </span>
                      {store.shop === activeStore?.shop && (
                        <Check className="w-4 h-4 text-teal-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-amber-500 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {confirmAction?.type === 'delete' ? 'Confirm Delete' : 'Confirm Action'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {confirmAction?.type === 'delete'
                    ? 'Are you sure you want to delete this item? This action cannot be undone.'
                    : (confirmAction as any)?.message || 'Are you sure you want to proceed?'}
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowConfirmDialog(false); setConfirmAction(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmAction?.type === 'delete') {
                    console.log('Deleting item:', (confirmAction as any).data);
                  }
                  setShowConfirmDialog(false);
                  setConfirmAction(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                {confirmAction?.type === 'delete' ? 'Delete' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}